import React, { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    Box,
    Typography,
    CircularProgress,
    Divider,
    Chip,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';

export interface TopUpRecord {
    id: string;
    user_id: string;
    amount: number;
    currency?: string;
    user?: { id?: string; email?: string; name?: string };
    payment_method?: string;
    timestamp?: string;
}

interface RefundTopUpDialogProps {
    open: boolean;
    topUp: TopUpRecord | null;
    onClose: () => void;
    onSuccess: () => void;
}

interface PreviewResponse {
    recommendation: 'refund' | 'void' | 'reject';
    reject_reason?: string;
    blockers: string[];
    warnings: string[];
    gateway: {
        gateway_code: string | null;
        gateway_order_id: string | null;
        order_status: string | null;
        query_error?: string;
    };
    wallet: {
        balance: number;
        available_balance: number;
        total_holds: number;
        projected_after: number;
        currency: string;
    } | null;
    refund_amount: number;
    currency: string;
}

const formatMoney = (amount: number, currency = 'HKD') =>
    `${currency} ${Number(amount || 0).toLocaleString('en-HK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const blockerLabel = (key: string, t: (k: string, opts?: any) => string): string => {
    const map: Record<string, string> = {
        insufficient_available_balance: 'Wallet does not have enough available balance (after pre-authorisation holds) for this refund',
        already_fully_refunded: 'This top-up has already been fully refunded',
        already_partially_refunded: 'This top-up has already had a partial refund — BBMSL allows only one refund per order, so no further refund can be issued. Manual reconciliation required if more is owed.',
        partial_void_not_supported: 'BBMSL\'s void operation cancels the whole order — partial void is not supported. Use the full refund amount, or wait for the order to settle and then issue a partial refund.',
        no_payment_session: 'No completed payment session was found for this top-up — manual reconciliation required',
        primary_wallet_not_found: 'No primary wallet exists for this user — manual reconciliation required',
        wallet_not_active: 'The user\'s primary wallet is not active',
        wallet_status_suspended: 'The user\'s primary wallet is suspended — refund cannot proceed until it is reactivated',
        wallet_status_closed: 'The user\'s primary wallet is closed — manual reconciliation required',
        wallet_lookup_failed: 'Wallet balance lookup failed',
        user_not_found: 'User account not found',
        user_account_deleted: 'User account has been deleted — refund row cannot reference a tombstoned user',
        not_a_topup: 'Transaction is not a top-up',
        transaction_not_completed: 'Transaction is not in the completed state',
        transaction_already_refunded: 'This top-up has already been refunded — its status is "refunded". BBMSL allows only one refund per order.',
        amount_exceeds_original: 'Refund amount exceeds the original top-up amount',
        already_refunded: 'BBMSL reports this order as already REFUNDED',
        already_voided: 'BBMSL reports this order as VOIDED',
        order_not_paid: 'BBMSL reports this order as still OPEN — payment was never completed',
        order_closed: 'BBMSL reports this order as CLOSED — no further actions allowed',
        no_gateway_link: 'No BBMSL order id is linked to this top-up',
    };
    // Fallback for parametric blockers like wallet_status_suspended that we
    // haven't put a static entry for.
    if (!(key in map)) {
        if (key.startsWith('wallet_status_')) {
            return `Wallet status is ${key.slice('wallet_status_'.length)} — refund cannot proceed`;
        }
    }
    return t(`resources.app-topups.refund.blockers.${key}`, { _: map[key] || key });
};

export const RefundTopUpDialog: React.FC<RefundTopUpDialogProps> = ({ open, topUp, onClose, onSuccess }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [reason, setReason] = useState('');
    const [preview, setPreview] = useState<PreviewResponse | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const topUpAmount = Number(topUp?.amount ?? 0);
    const currency = topUp?.currency ?? 'HKD';

    const recommendation = preview?.recommendation;
    const isVoid = recommendation === 'void';
    const isRefund = recommendation === 'refund';
    const canProceed = (isVoid || isRefund) && (preview?.blockers.length === 0);

    const fetchPreview = useCallback(async () => {
        if (!topUp) return;
        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const token = await tokenManager.getAccessToken();
            const resp = await fetch(`${API_CONFIG.baseURL}/api/backend/app-topups/refund/preview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                credentials: 'include',
                body: JSON.stringify({
                    transaction_id: topUp.id,
                    amount: topUpAmount,
                }),
            });
            if (!resp.ok) {
                const txt = await resp.text().catch(() => '');
                throw new Error(`HTTP ${resp.status}${txt ? `: ${txt.slice(0, 200)}` : ''}`);
            }
            const body = await resp.json();
            setPreview(body?.data ?? null);
        } catch (e: any) {
            setPreviewError(String(e?.message || e));
            setPreview(null);
        } finally {
            setPreviewLoading(false);
        }
    }, [topUp, topUpAmount]);

    useEffect(() => {
        if (!open) {
            setReason('');
            setPreview(null);
            setPreviewError(null);
            return;
        }
        fetchPreview();
    }, [open, fetchPreview]);

    const handleSubmit = useCallback(async () => {
        if (!topUp || !reason.trim() || !preview) return;
        setSubmitting(true);
        try {
            const token = await tokenManager.getAccessToken();
            const resp = await fetch(`${API_CONFIG.baseURL}/api/backend/app-topups/refund`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                credentials: 'include',
                body: JSON.stringify({
                    transaction_id: topUp.id,
                    amount: topUpAmount,
                    reason: reason.trim(),
                    notify_user: true,
                    expected_action: preview.recommendation,
                }),
            });

            if (resp.status === 207) {
                const body = await resp.json().catch(() => ({}));
                notify(
                    body?.warning || 'Refund partially completed — manual reconciliation required',
                    { type: 'warning', autoHideDuration: 12000 },
                );
                onSuccess();
                onClose();
                return;
            }

            if (resp.status === 409) {
                // STATE_CHANGED — re-preview, force operator to re-confirm
                const body = await resp.json().catch(() => ({}));
                notify(
                    body?.error || 'Order status changed since preview — please re-confirm',
                    { type: 'warning', autoHideDuration: 8000 },
                );
                await fetchPreview();
                return;
            }

            if (!resp.ok) {
                const errorBody = await resp.json().catch(() => ({}));
                const message = errorBody?.error || errorBody?.message || `HTTP ${resp.status}`;
                notify(message, { type: 'error', autoHideDuration: 10000 });
                return;
            }

            const body = await resp.json();
            const action = body?.gateway_action || preview.recommendation;
            notify(
                action === 'void'
                    ? translate('resources.app-topups.refund.successVoid', { _: 'Top-up voided successfully' })
                    : translate('resources.app-topups.refund.successRefund', { _: 'Refund processed successfully' }),
                { type: 'success' },
            );
            onSuccess();
            onClose();
        } catch (error: any) {
            notify(`${translate('resources.app-topups.refund.failed', { _: 'Refund failed' })}: ${error?.message || error}`, {
                type: 'error',
                autoHideDuration: 8000,
            });
        } finally {
            setSubmitting(false);
        }
    }, [topUp, topUpAmount, reason, preview, onSuccess, onClose, notify, translate, fetchPreview]);

    if (!topUp) return null;

    const wallet = preview?.wallet;
    const orderStatus = preview?.gateway.order_status;

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isVoid
                    ? translate('resources.app-topups.refund.titleVoid', { _: 'Void top-up' })
                    : translate('resources.app-topups.refund.title', { _: 'Refund top-up' })}
            </DialogTitle>
            <DialogContent>
                {previewLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4, justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            {translate('resources.app-topups.refund.checkingStatus', { _: 'Checking BBMSL status and wallet balance...' })}
                        </Typography>
                    </Box>
                )}

                {previewError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                            {translate('resources.app-topups.refund.previewFailedTitle', { _: 'Could not check refund eligibility' })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{previewError}</Typography>
                    </Alert>
                )}

                {preview && (
                    <>
                        {/* Top row — top-up amount + BBMSL order status */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {translate('resources.app-topups.refund.topUpAmount', { _: 'Top-up amount' })}
                                </Typography>
                                <Typography variant="h6" fontWeight={700}>
                                    {formatMoney(topUpAmount, currency)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {translate('resources.app-topups.refund.bbmslStatus', { _: 'BBMSL order status' })}
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    {orderStatus ? (
                                        <Chip
                                            label={orderStatus}
                                            size="small"
                                            color={orderStatus === 'SETTLED' ? 'warning' : orderStatus === 'SUCCESS' ? 'info' : 'default'}
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Wallet row */}
                        {wallet && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.app-topups.refund.walletBalance', { _: 'Wallet balance' })}
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {formatMoney(wallet.balance, wallet.currency)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.app-topups.refund.preAuthHolds', { _: 'Pre-auth holds' })}
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {formatMoney(wallet.total_holds, wallet.currency)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.app-topups.refund.availableForRefund', { _: 'Available' })}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight={700}
                                        sx={{ color: wallet.available_balance < topUpAmount ? 'error.main' : 'success.main' }}
                                    >
                                        {formatMoney(wallet.available_balance, wallet.currency)}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {/* Recommendation alert */}
                        {isVoid && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {translate('resources.app-topups.refund.willVoidTitle', { _: 'This payment has not yet been settled' })}
                                </Typography>
                                <Typography variant="body2">
                                    {translate('resources.app-topups.refund.willVoidBody', {
                                        _: `BBMSL reports the order as SUCCESS (paid but not yet settled to the merchant). We'll send a VOID — the authorisation is cancelled and no funds need to flow back. The user's wallet will be debited ${formatMoney(topUpAmount, currency)}.`,
                                    })}
                                </Typography>
                            </Alert>
                        )}

                        {isRefund && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {translate('resources.app-topups.refund.willRefundTitle', { _: 'This payment has already been settled — a refund will be issued' })}
                                </Typography>
                                <Typography variant="body2">
                                    {translate('resources.app-topups.refund.willRefundBody', {
                                        _: `BBMSL reports the order as SETTLED. Because the funds have already moved to the merchant we cannot void — we have to issue a REFUND instead. The cardholder typically sees the money returned 5-10 business days after the refund is approved. The user's wallet will be debited ${formatMoney(topUpAmount, currency)} immediately.`,
                                    })}
                                </Typography>
                            </Alert>
                        )}

                        {recommendation === 'reject' && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {translate('resources.app-topups.refund.cannotProceedTitle', { _: 'Cannot refund this top-up' })}
                                </Typography>
                                {(preview.blockers.length > 0 ? preview.blockers : preview.reject_reason ? [preview.reject_reason] : []).map((b) => (
                                    <Typography key={b} variant="body2">• {blockerLabel(b, translate)}</Typography>
                                ))}
                            </Alert>
                        )}

                        {/* Non-blocking warnings (gateway_query_failed,
                            wallet_currency_*_differs_from_topup_*, …). The
                            "settled, will refund" warning is omitted here
                            because the orange WARNING alert above already
                            covers that case explicitly. */}
                        {preview.warnings.filter(w => w !== 'order_already_settled_will_refund_not_void').length > 0 && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {preview.warnings
                                    .filter(w => w !== 'order_already_settled_will_refund_not_void')
                                    .map((w) => {
                                        let copy = w.replace(/_/g, ' ');
                                        const m = w.match(/^wallet_currency_(\w+)_differs_from_topup_(\w+)$/);
                                        if (m) {
                                            copy = `Heads up: the user's primary wallet is in ${m[1]} but this top-up is in ${m[2]}. The refund will debit the ${m[1]} wallet.`;
                                        } else if (w === 'gateway_query_failed') {
                                            copy = 'BBMSL status query failed — falling back to refund (refund is the safe default; BBMSL itself will reject if void was actually required).';
                                        } else if (w === 'gateway_status_unknown_assuming_settled') {
                                            copy = 'Could not read order status from BBMSL; assuming settled and using refund. If BBMSL rejects with "use void", retry from a fresh preview.';
                                        }
                                        return (<Typography key={w} variant="body2">• {copy}</Typography>);
                                    })}
                            </Alert>
                        )}
                    </>
                )}

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    required
                    autoFocus
                    label={translate('resources.app-topups.refund.reason', { _: 'Reason (required)' })}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={submitting || !canProceed}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    {translate('resources.app-topups.refund.cancel', { _: 'Cancel' })}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color={isVoid ? 'primary' : 'warning'}
                    disabled={submitting || !canProceed || !reason.trim() || previewLoading}
                >
                    {submitting && <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />}
                    {isVoid
                        ? translate('resources.app-topups.refund.confirmVoid', { _: 'Confirm void' })
                        : translate('resources.app-topups.refund.confirmRefund', { _: 'Confirm refund' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RefundTopUpDialog;
